import type { AvatarProps, BlockDefinition } from '../types';
import { escapeHtml, safeUrl } from '../html';

/**
 * Avatar block. Clean circular profile photo with one optional overlay:
 *   - showOnlineDot: static green dot bottom-right (Discord style)
 *
 * Both `verified` and `storyRing` were removed:
 *   - Verified badges belong inline next to a name — use `heading.verified`
 *     or the `instagram-bio` composite block.
 *   - The IG story ring is sized correctly inside `instagram-bio`. On the
 *     standalone avatar at 96px the gap-to-photo ratio looked off, so we
 *     keep it only where it reads well.
 */
export const avatar: BlockDefinition<AvatarProps> = {
  type: 'avatar',
  meta: {
    label: 'Avatar',
    icon: '👤',
    category: 'profile',
    description: 'Circular profile image with optional online dot.',
  },
  defaults: () => ({
    src: '',
    initials: '',
    size: 'md',
    cropX: 50,
    cropY: 50,
    showOnlineDot: false,
  }),
  validate(input) {
    const i = (input ?? {}) as Partial<AvatarProps>;
    const clamp01to100 = (v: unknown, fallback: number): number => {
      if (typeof v !== 'number' || Number.isNaN(v)) return fallback;
      return Math.max(0, Math.min(100, v));
    };
    return {
      src: typeof i.src === 'string' ? i.src : '',
      initials: typeof i.initials === 'string' ? i.initials.slice(0, 3) : '',
      ringColor: typeof i.ringColor === 'string' ? i.ringColor.replace(/^#/, '') : undefined,
      size: ['sm', 'md', 'lg'].includes(i.size as string) ? (i.size as AvatarProps['size']) : 'md',
      cropX: clamp01to100(i.cropX, 50),
      cropY: clamp01to100(i.cropY, 50),
      showOnlineDot: !!i.showOnlineDot,
    };
  },
  render(props) {
    const sizePx = props.size === 'sm' ? 64 : props.size === 'lg' ? 128 : 96;
    const ring = props.ringColor ? `box-shadow: 0 0 0 3px #${props.ringColor};` : '';
    const cropX = typeof props.cropX === 'number' ? props.cropX : 50;
    const cropY = typeof props.cropY === 'number' ? props.cropY : 50;

    const containerStyle = `position:relative;display:inline-block;width:${sizePx}px;height:${sizePx}px;`;
    const circleStyle = `width:${sizePx}px;height:${sizePx}px;border-radius:9999px;${ring}`;

    // Online dot — Discord-style static green circle with a white outer
    // ring. Bottom-right is the conventional position.
    const dotPx = Math.max(16, Math.round(sizePx * 0.26));
    const dotRing = Math.max(2, Math.round(dotPx * 0.18));

    const onlineOverlay = props.showOnlineDot
      ? `<span aria-label="Online" title="Online" style="position:absolute;bottom:2px;right:2px;width:${dotPx}px;height:${dotPx}px;background:#fff;border-radius:9999px;display:flex;align-items:center;justify-content:center;">
  <span style="display:block;width:${dotPx - dotRing * 2}px;height:${dotPx - dotRing * 2}px;background:#22c55e;border-radius:9999px;"></span>
</span>`
      : '';

    let mainCircle: string;
    if (props.src) {
      mainCircle = `<img src="${safeUrl(props.src)}" alt="" style="${circleStyle}object-fit:cover;object-position:${cropX}% ${cropY}%;display:block;" loading="lazy" decoding="async" />`;
    } else {
      const initials = escapeHtml(props.initials || '?').toUpperCase();
      mainCircle = `<div style="${circleStyle}background:rgba(0,0,0,0.06);display:flex;align-items:center;justify-content:center;font-size:${Math.floor(sizePx * 0.4)}px;font-weight:600;color:var(--text);">${initials}</div>`;
    }

    return `<div class="pb-block pb-avatar" style="text-align:center;">
  <span style="${containerStyle}">
    ${mainCircle}
    ${onlineOverlay}
  </span>
</div>`;
  },
};
